import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { Panel } from '../../components/layout/Panel';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { ReferencedWorkDTO, WorkHasPartDTO } from '../../../../api/core';
import { getDefaultWorkPart } from '../../../../Settings/emptyModels';
import React, { ReactElement, ReactNode } from 'react';
import { FieldSettings } from '../../../../util/types';
import { PubtypeUtils } from '../../../../util/pubtype-utils';
import { Fieldset } from '../../components/layout/Fieldset';
import { InputGroup } from '../../components/layout/InputGroup';
import { LabelledWorkSuggester } from '../../../components/form/LabelledWorkSuggester';
import { LabelledInputText } from '../../../components/form/LabelledInputText';

export const HasPartPage = (props: Props): ReactElement => {
  return <Block title={`work.header.haspart`}>
    <PermissionGate always={false} entity={props.permissionEntity} field="work_has_parts">
      {(checkPermission: CheckPermissionFunction) =>
        <Panel key={'hasPart'} className={'col-12'} title={'work.attr.hasPart'}>
          <MultiInputControls<WorkHasPartDTO>
            canEdit={checkPermission().canWrite}
            lines={props.modifiedModel.workHasParts}
            addNew={(rows: WorkHasPartDTO[]): number => rows.push(getDefaultWorkPart())}
            onChange={(parts: WorkHasPartDTO[]): void => props.setField('workHasParts', parts)}
            renderLines={(parent: WorkHasPartDTO, onLineChange: (newValues: WorkHasPartDTO) => void, actions: Actions, idx: number): ReactNode => {
              const fieldSettings: FieldSettings = parent?.referencedWork?.pubtype ?
                PubtypeUtils.getIsPartOfFields(PubtypeUtils.getGroupByPubtype(parent.referencedWork.pubtype), props.pubtypeGroup) :
                {pageFirst: false, pageLast: false, volume: false, issue: false, articleId: false};
              return <Fieldset className={'flex flex-column gap-5'}>
                <InputGroup
                  delete={{
                    action: actions.delete,
                    disabled: !checkPermission().canWrite,
                  }}
                >
                  <LabelledWorkSuggester
                    id={`hasPart-${idx}-work`}
                    autoFocus={props.autoFocus}
                    value={parent.referencedWork ? [parent.referencedWork] : []}
                    validation={props.validation?.root?.fields?.work_has_parts.children[idx].fields?.referenced_work}
                    permissions={checkPermission('referenced_work')}
                    onChange={(referencedWorks: ReferencedWorkDTO[]): void => onLineChange({
                      referencedWork: referencedWorks.length > 0 ? referencedWorks.pop() : undefined,
                      issue: undefined,
                      pageFirst: undefined,
                      pageLast: undefined,
                      articleId: undefined,
                      volume: undefined,
                    })}
                    label={'work.attr.part.attr.referencedWork'}
                    multiple={false}
                  />
                </InputGroup>
                <InputGroup>
                  <LabelledInputText
                    id={`hasPart-${idx}-volume`}
                    value={parent.volume || ''}
                    permissions={checkPermission('volume')}
                    validation={props.validation?.root?.fields?.work_has_parts.children[idx].fields?.volume}
                    onChange={(volume: string): void => onLineChange({...parent, volume})}
                    label={'work.attr.part.attr.volume'}
                    disabled={!fieldSettings.volume}
                  />
                  <LabelledInputText
                    id={`hasPart-${idx}-issue`}
                    value={parent.issue || ''}
                    permissions={checkPermission('issue')}
                    validation={props.validation?.root?.fields?.work_has_parts.children[idx].fields?.issue}
                    onChange={(issue: string): void => onLineChange({...parent, issue})}
                    label={'work.attr.part.attr.issue'}
                    disabled={!fieldSettings.issue}
                  />
                  <LabelledInputText
                    id={`hasPart-${idx}-pageFirst`}
                    value={parent.pageFirst || ''}
                    permissions={checkPermission('page_first')}
                    validation={props.validation?.root?.fields?.work_has_parts.children[idx].fields?.page_first}
                    onChange={(pageFirst: string): void => onLineChange({...parent, pageFirst})}
                    label={'work.attr.part.attr.pageFirst'}
                    disabled={!fieldSettings.pageFirst}
                  />
                  <LabelledInputText
                    id={`hasPart-${idx}-pageLast`}
                    value={parent.pageLast || ''}
                    permissions={checkPermission('page_last')}
                    validation={props.validation?.root?.fields?.work_has_parts.children[idx].fields?.page_last}
                    onChange={(pageLast: string): void => onLineChange({...parent, pageLast})}
                    label={'work.attr.part.attr.pageLast'}
                    disabled={!fieldSettings.pageLast}
                  />
                  <LabelledInputText
                    id={`hasPart-${idx}-articleId`}
                    value={parent.articleId || ''}
                    permissions={checkPermission('article_id')}
                    validation={props.validation?.root?.fields?.work_has_parts.children[idx].fields?.article_id}
                    onChange={(articleId: string): void => onLineChange({...parent, articleId})}
                    label={'work.attr.part.attr.articleId'}
                    disabled={!fieldSettings.articleId}
                  />
                </InputGroup>
              </Fieldset>;
            }}
          />
        </Panel>
      }
    </PermissionGate>
  </Block>;
};
